import React, { useState, useEffect} from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import logger from '../../utils/logger';
import { toast, ToastContainer } from 'react-toastify';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import BreadCrumb from '../BreadCrumb';
import { useAuth } from '../Auth/Auth';
import { XCircleIcon } from '@heroicons/react/24/solid';
import { JobDetails } from './Subs/JobDetails';

const pages = [

    { name: 'Procurement', to: '/procurement', component: Link, current: false },
    { name: 'Job Card Pricing', to: '/procurement/jobcardpricing', component: Link, current: false },
    { name: 'Job Card Cost', to: '/procurement/jobcardpricing/jobcost', component: Link, current: true },
  ];


const ProcQuotationPage = () => {

  const location = useLocation();
  const job = location.state?.job;
  const [parts, setParts] = useState(job?.partsRequired || []);
  const [services, setServices] = useState(
    (job?.servicesRequired || []).map(service => ({
      ...service, bestCostPrice: parseInt(service.serviceCost, 10) || 0
    }))
  );
  const  {user, role}  = useAuth();
  const navigate = useNavigate();
  const currentUser = user?.email;



  useEffect(() => {
    if (job && job.servicesRequired) {
      setServices(job.servicesRequired.map(service => ({
        ...service,
        bestCostPrice: parseInt(service.serviceCost, 10) || 0
      })));
    }
  }, [job]);


  const handlePriceChange = (index, value, type, priceType) => {
    logger.info(`Updating ${priceType} for ${type} at index ${index} to value: `, value);

    const numericValue = parseFloat(value);
    if (!isNaN(numericValue)) {
      const updatedItems = type === 'part' ? [...parts] : [...services];
      if (priceType === 'best') {
        updatedItems[index].bestCostPrice = numericValue;
      } else if (priceType === 'worst') {
        updatedItems[index].worstCostPrice = numericValue;
      }
      type === 'part' ? setParts(updatedItems) : setServices(updatedItems);
    } else {
      // Handle invalid input, e.g., reset to previous value or set to a default like 0
      logger.error('Invalid number input for price');
    }
  };

  const handlePartQuantityChange = (index, value) => {
    const updatedParts = [...parts];
    updatedParts[index].quantity = Number(value);
    setParts(updatedParts);
};

const handleServiceQuantityChange = (index, value) => {
    const updatedServices = [...services];
    updatedServices[index].serviceQuantity = Number(value);
    setServices(updatedServices);
};


const handleSaveQuotation = async () => {
  const isValidPrice = (price) => !isNaN(parseFloat(price)) && isFinite(price);

const isAllPartsValid = parts.every(part => isValidPrice(part.bestCostPrice) && isValidPrice(part.worstCostPrice));
const isAllServicesValid = services.every(service => isValidPrice(service.bestCostPrice) && isValidPrice(service.worstCostPrice));

if (!isAllPartsValid || !isAllServicesValid) {
  toast.error("Please fill all the cost price fields with valid numbers before saving.");
  return;
}


  try {
    const jobRef = doc(db, 'jobs', job.id);
    await updateDoc(jobRef, {
      partsRequired: parts.map(part => ({
        partName: part.partName,
        quantity: Number(part.quantity),
        bestCostPrice: Number(part.bestCostPrice),
        worstCostPrice: Number(part.worstCostPrice),
      })),
      servicesRequired: services.map(service => ({
        serviceType: service.serviceType,
        technicianType: service.technicianType,
        serviceCost: service.serviceCost,
        serviceQuantity: Number(service.serviceQuantity),
        bestCostPrice: Number(service.bestCostPrice),
        worstCostPrice: Number(service.worstCostPrice),
      })),
      procurementPricing: "Set",
      pricingProvidedBy : currentUser
    });
    toast.success("Cost prices saved successfully!");
    setTimeout(() => navigate('/procurement/jobcardpricing'), 2000);
  } catch (error) {
    logger.error("Error saving cost prices: ", error);
    toast.error("Failed to save cost prices.");
  }
};



  const handleRemoveItem = (index, type) => {
    if (type === 'part') {
        setParts(parts.filter((_, idx) => idx !== index));
    } else {
        setServices(services.filter((_, idx) => idx !== index));
    }
};

  // Role guard
  if (role !== 'Admin' && role !== 'Finance' && role !== 'Procurement') {
    return <Navigate to="/unauthorized" replace />;
  }

  // Null guard — redirect if navigated directly without job state
  if (!job || !job.id) {
    return (
      <>
        <BreadCrumb pages={pages} />
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <p className="text-gray-500">No job data available. Please select a job from the Job Card Pricing page.</p>
          <Link to="/procurement/jobcardpricing" className="mt-4 inline-block text-blue-600 hover:underline">
            Go to Job Card Pricing
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Add Cost Price to Job Card</h1>
          <div className="bg-white rounded-xl shadow p-4">
            <JobDetails job={job} />
          </div>
        </header>

        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '80px' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '120px' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '50px' }} />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Qty / Duration
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Best Cost Price
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Worst Cost Price
                  </th>
                  <th scope="col" className="relative px-2 py-3">
                    <span className="sr-only">Delete</span>
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {parts.map((part, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm text-gray-700">Part</td>
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900 truncate">{part.partName}</div>
                    </td>
                    <td className="px-4 py-4">
                      <input
                        type="number"
                        value={part.quantity}
                        onChange={(e) => handlePartQuantityChange(index, e.target.value)}
                        className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        required
                        value={part.bestCostPrice}
                        onChange={(e) => handlePriceChange(index, e.target.value, 'part', 'best')}
                        placeholder="Enter best price"
                        className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        value={part.worstCostPrice}
                        onChange={(e) => handlePriceChange(index, e.target.value, 'part', 'worst')}
                        placeholder="Enter worst price"
                        className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="px-2 py-4"></td>
                  </tr>
                ))}
                {services.map((service, index) => (
                  <tr key={`service-${index}`} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm text-gray-700">Service</td>
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900 truncate">{service.serviceType}</div>
                    </td>
                    <td className="px-4 py-4">
                      <input
                        type="number"
                        value={service.serviceQuantity}
                        onChange={(e) => handleServiceQuantityChange(index, e.target.value)}
                        className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        required
                        value={service.bestCostPrice}
                        onChange={(e) => handlePriceChange(index, e.target.value, 'service', 'best')}
                        placeholder="Enter best price"
                        className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        value={service.worstCostPrice}
                        onChange={(e) => handlePriceChange(index, e.target.value, 'service', 'worst')}
                        placeholder="Enter worst price"
                        className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="px-2 py-4 text-center">
                      <button onClick={() => handleRemoveItem(index, 'service')} className="text-red-500 hover:text-red-700 transition-colors">
                        <XCircleIcon className="h-6 w-6" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-4 bg-gray-50 border-t border-gray-200">
            <button
              type="button"
              onClick={handleSaveQuotation}
              className="inline-flex justify-center py-2.5 px-6 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Save Cost
            </button>
          </div>
        </div>

        <ToastContainer />
      </div>
    </>
  );
};

export default ProcQuotationPage;
